import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiCheck, FiLoader } from "react-icons/fi";
import "./MentionDropdown.css";

/**
 * MentionDropdown - Autocomplete dropdown for @mentions
 * 
 * @param {Array} suggestions - List of user suggestions
 * @param {Function} onSelect - Called when user selects a suggestion
 * @param {Function} onClose - Called when dropdown should close
 * @param {boolean} loading - Show loading state
 * @param {Object} position - Optional { top, left } for positioning
 */
const MentionDropdown = ({
  suggestions = [],
  onSelect,
  onClose,
  loading = false,
  position = null,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dropdownRef = useRef(null);
  const itemRefs = useRef([]);

  // Reset selection when suggestions change
  useEffect(() => {
    setSelectedIndex(0);
  }, [suggestions]);

  // Scroll selected item into view
  useEffect(() => {
    if (itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [selectedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (!suggestions.length) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
        case "Enter":
        case "Tab":
          e.preventDefault();
          if (suggestions[selectedIndex]) {
            onSelect(suggestions[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose?.();
          break;
        default:
          break;
      }
    },
    [suggestions, selectedIndex, onSelect, onClose]
  );

  // Attach keyboard listener
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Get user initials for avatar fallback
  const getUserInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (!suggestions.length && !loading) return null;

  return (
    <AnimatePresence>
      <motion.div
        ref={dropdownRef}
        className="mention-dropdown"
        style={position ? { top: position.top, left: position.left } : undefined}
        initial={{ opacity: 0, y: -8, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.95 }}
        transition={{ duration: 0.15 }}
      >
        {loading ? (
          <div className="mention-dropdown__loading">
            <FiLoader className="mention-dropdown__spinner" />
            <span>Searching...</span>
          </div>
        ) : (
          <ul className="mention-dropdown__list">
            {suggestions.map((user, index) => (
              <li
                key={user._id}
                ref={(el) => (itemRefs.current[index] = el)}
                className={`mention-dropdown__item ${
                  index === selectedIndex ? "mention-dropdown__item--selected" : ""
                }`}
                onClick={() => onSelect(user)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="mention-dropdown__avatar">
                  {user.profilePhotoUrl ? (
                    <img
                      src={user.profilePhotoUrl}
                      alt={user.name}
                      className="mention-dropdown__avatar-img"
                    />
                  ) : (
                    <div className="mention-dropdown__avatar-initials">
                      {getUserInitials(user.name)}
                    </div>
                  )}
                </div>
                <div className="mention-dropdown__info">
                  <div className="mention-dropdown__name">
                    {user.name}
                    {user.isVerified && (
                      <FiCheck className="mention-dropdown__verified" />
                    )}
                  </div>
                  <div className="mention-dropdown__role">
                    {user.professionalRole || user.field || "Researcher"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="mention-dropdown__hint">
          <kbd>↑</kbd> <kbd>↓</kbd> to navigate, <kbd>Enter</kbd> to select
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MentionDropdown;
